import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Lock, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { inputCls, UploadField } from "./ProjectsPanel";

const labelCls = "block font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1.5";

export function DocumentsPanel({ documents, reload }) {
  const empty = { title: "", description: "", category: "", pages: 0, access: "controlled", enabled: true, file_url: "", private_file: "", cover_url: "", title_pt: "", description_pt: "", category_pt: "" };
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const startEdit = (d) => {
    setForm({
      title: d.title, description: d.description || "", category: d.category || "", pages: d.pages || 0,
      access: d.access || "controlled", enabled: d.enabled !== false,
      file_url: d.file_url || "", private_file: d.private_file || "", cover_url: d.cover_url || "",
      title_pt: d.title_pt || "", description_pt: d.description_pt || "", category_pt: d.category_pt || "",
    });
    setEditing(d.id);
  };
  const startNew = () => { setForm(empty); setEditing("new"); };

  const save = async () => {
    try {
      const payload = { ...form, pages: Number(form.pages) || 0 };
      if (payload.access === "public") payload.private_file = "";
      else payload.file_url = "";
      if (editing === "new") await api.post("/admin/documents", { ...payload, order: documents.length });
      else await api.put(`/admin/documents/${editing}`, payload);
      toast.success("Document saved");
      setEditing(null);
      reload();
    } catch { toast.error("Save failed"); }
  };

  const toggleEnabled = async (d) => {
    await api.put(`/admin/documents/${d.id}`, {
      title: d.title, description: d.description || "", category: d.category || "", pages: d.pages || 0,
      access: d.access || "controlled", enabled: !(d.enabled !== false),
      file_url: d.file_url || "", private_file: d.private_file || "", cover_url: d.cover_url || "", order: d.order || 0,
    });
    toast.success(d.enabled === false ? "Document enabled" : "Document disabled");
    reload();
  };

  const available = (d) => d.enabled !== false && ((d.access || "controlled") === "public" ? !!d.file_url : !!d.private_file);
  const accessLabel = (a) => (a === "public" ? "Public" : a === "confidential" ? "Confidential" : "Controlled Access");

  return (
    <div data-testid="documents-panel">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-serif text-2xl text-ivory">Investor Data Room</h2>
          <p className="text-sm text-white/40 mt-1">Upload, replace and manage investor documents and their access level.</p>
        </div>
        <button data-testid="document-add-btn" onClick={startNew} className="flex items-center gap-2 px-4 py-2.5 bg-gold text-ink font-mono text-[10px] tracking-[0.2em] uppercase hover:bg-gold-light"><Plus size={13} /> Add Document</button>
      </div>

      {editing !== null && (
        <div data-testid="document-form" className="border border-gold/30 bg-surface p-6 mb-8 grid sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>Title</label><input data-testid="document-title-input" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div><label className={labelCls}>Category</label><input data-testid="document-category-input" placeholder="e.g. Strategy & Investment" className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Description</label><textarea data-testid="document-description-input" rows={2} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="sm:col-span-2 border-t border-white/10 pt-4">
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-gold mb-3">Português (PT) — optional translations</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>Title (PT)</label><input data-testid="document-title-pt-input" className={inputCls} value={form.title_pt || ""} onChange={(e) => setForm({ ...form, title_pt: e.target.value })} /></div>
              <div><label className={labelCls}>Category (PT)</label><input data-testid="document-category-pt-input" className={inputCls} value={form.category_pt || ""} onChange={(e) => setForm({ ...form, category_pt: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Description (PT)</label><textarea data-testid="document-description-pt-input" rows={2} className={inputCls} value={form.description_pt || ""} onChange={(e) => setForm({ ...form, description_pt: e.target.value })} /></div>
            </div>
          </div>
          <div><label className={labelCls}>Pages</label><input data-testid="document-pages-input" type="number" min={0} className={inputCls} value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} /></div>
          <div>
            <label className={labelCls}>Access Level</label>
            <select data-testid="document-access-select" className={inputCls} value={form.access} onChange={(e) => setForm({ ...form, access: e.target.value })}>
              <option value="public">Public — directly viewable on the website</option>
              <option value="controlled">Controlled — granted via secure link</option>
              <option value="confidential">Confidential — highest restriction, secure link only</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>{form.access === "public" ? "File (public)" : "Confidential File (never publicly exposed)"}</label>
            <UploadField
              testid="document-file-input"
              accept=".pdf,image/*"
              privateUpload={form.access !== "public"}
              value={form.access === "public" ? form.file_url : form.private_file}
              onChange={(v, data) => setForm((f) => (f.access === "public"
                ? { ...f, file_url: v, cover_url: data?.cover_url || f.cover_url }
                : { ...f, private_file: v, cover_url: data?.cover_url || f.cover_url }))}
            />
            {form.cover_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${form.cover_url}`} alt="Cover preview" className="mt-3 h-32 border border-white/10 object-cover object-top" />}
            <p className="text-xs text-white/30 mt-2">
              {form.access !== "public"
                ? "Stored in private storage. Investors receive time-limited secure links only after you grant access. PDF cover preview is generated automatically."
                : "Leave empty to show the resource as 'In Preparation' on the website."}
            </p>
          </div>
          <label className="sm:col-span-2 flex items-center gap-3 text-sm text-white/60">
            <input data-testid="document-enabled-toggle" type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="accent-[#D4AF37]" />
            Enabled (listed on the website and available for access grants)
          </label>
          <div className="sm:col-span-2 flex gap-3">
            <button data-testid="document-save-btn" onClick={save} className="px-6 py-2.5 bg-gold text-ink font-mono text-[10px] tracking-[0.2em] uppercase hover:bg-gold-light">Save</button>
            <button onClick={() => setEditing(null)} className="px-6 py-2.5 border border-white/15 text-white/60 font-mono text-[10px] tracking-[0.2em] uppercase">Cancel</button>
          </div>
        </div>
      )}

      <div className="divide-y divide-white/10 border border-white/10">
        {documents.map((d) => (
          <div key={d.id} data-testid={`document-row-${d.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-center gap-4 px-5 py-4 bg-ink">
            {d.cover_url
              ? <img src={`${process.env.REACT_APP_BACKEND_URL}${d.cover_url}`} alt="" className="w-10 h-12 object-cover object-top border border-white/10 shrink-0" />
              : ((d.access || "controlled") === "public" ? <Globe size={15} className="text-emerald-400 shrink-0" /> : <Lock size={15} className="text-gold shrink-0" />)}
            <div className="flex-1 min-w-0">
              <p className="text-ivory font-medium truncate">{d.title} {d.enabled === false && <span className="text-red-300/70 text-xs">(disabled)</span>}</p>
              <p className="text-xs text-white/40">
                {accessLabel(d.access)} · {available(d) ? `file attached${d.pages ? ` · ${d.pages} pages` : ""}` : "In Preparation"}
              </p>
            </div>
            <button data-testid={`document-toggle-${d.id}`} onClick={() => toggleEnabled(d)} className="font-mono text-[9px] tracking-[0.15em] uppercase text-white/40 hover:text-gold border border-white/10 px-3 py-1.5">
              {d.enabled === false ? "Enable" : "Disable"}
            </button>
            <button data-testid={`document-edit-${d.id}`} onClick={() => startEdit(d)} className="text-white/40 hover:text-gold p-1"><Pencil size={15} /></button>
            <button data-testid={`document-delete-${d.id}`} onClick={async () => { await api.delete(`/admin/documents/${d.id}`); reload(); }} className="text-white/40 hover:text-red-400 p-1"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function UpdatesPanel({ updates, reload }) {
  const [form, setForm] = useState({ title: "", body: "", published: false });

  const add = async () => {
    if (!form.title) return;
    await api.post("/admin/updates", form);
    toast.success("Update added");
    setForm({ title: "", body: "", published: false });
    reload();
  };

  return (
    <div data-testid="updates-panel">
      <h2 className="font-serif text-2xl text-ivory mb-6">Updates</h2>
      <div className="border border-white/10 bg-surface p-6 mb-8 grid gap-4">
        <input data-testid="update-title-input" placeholder="Update title" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <textarea data-testid="update-body-input" placeholder="Body (optional)" rows={2} className={inputCls} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
        <label className="flex items-center gap-3 text-sm text-white/60">
          <input data-testid="update-published-toggle" type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-[#D4AF37]" />
          Published (visible on website)
        </label>
        <button data-testid="update-add-btn" onClick={add} className="w-max px-6 py-2.5 bg-gold text-ink font-mono text-[10px] tracking-[0.2em] uppercase hover:bg-gold-light">Add Update</button>
      </div>
      <div className="divide-y divide-white/10 border border-white/10">
        {updates.map((u) => (
          <div key={u.id} data-testid={`update-row-${u.id}`} className="flex items-center gap-4 px-5 py-4 bg-ink">
            <div className="flex-1">
              <p className="text-ivory">{u.title} {!u.published && <span className="text-white/30 text-xs">(draft)</span>}</p>
              <p className="text-xs text-white/40">{new Date(u.created_at).toLocaleDateString()}</p>
            </div>
            <button data-testid={`update-delete-${u.id}`} onClick={async () => { await api.delete(`/admin/updates/${u.id}`); reload(); }} className="text-white/40 hover:text-red-400 p-1"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function InboxPanel({ messages, inquiries, accessRequests, reload }) {
  const markRead = async (collection, id) => {
    await api.put(`/admin/inbox/${collection}/${id}/read`);
    reload();
  };

  const Section = ({ title, items, collection, render }) => (
    <div className="mb-10">
      <h3 className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold mb-4">{title} ({items.length})</h3>
      {items.length === 0 && <p className="text-sm text-white/30">Nothing here yet.</p>}
      <div className="divide-y divide-white/10 border border-white/10">
        {items.map((m) => (
          <div key={m.id} data-testid={`inbox-row-${m.id}`} className={`px-5 py-4 bg-ink ${m.read ? "opacity-50" : ""}`}>
            {render(m)}
            {!m.read && (
              <button data-testid={`inbox-read-${m.id}`} onClick={() => markRead(collection, m.id)} className="mt-2 font-mono text-[9px] tracking-[0.15em] uppercase text-white/40 hover:text-gold border border-white/10 px-3 py-1">Mark read</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="inbox-panel">
      <h2 className="font-serif text-2xl text-ivory mb-6">Inbox</h2>
      <Section title="Contact Messages" items={messages} collection="contact_messages" render={(m) => (
        <div>
          <p className="text-ivory font-medium">{m.name} <span className="text-white/40 text-sm">· {m.email}</span></p>
          <p className="text-xs text-gold font-mono tracking-wider mt-0.5">{m.interest} {m.country && `· ${m.country}`} {m.company && `· ${m.company}`}</p>
          <p className="text-sm text-white/55 mt-2">{m.message}</p>
        </div>
      )} />
      <Section title="Partnership Inquiries" items={inquiries} collection="partnership_inquiries" render={(m) => (
        <div>
          <p className="text-ivory font-medium">{m.name} <span className="text-white/40 text-sm">· {m.email}</span></p>
          <p className="text-xs text-gold font-mono tracking-wider mt-0.5">{m.partner_type} {m.company && `· ${m.company}`}</p>
          {m.message && <p className="text-sm text-white/55 mt-2">{m.message}</p>}
        </div>
      )} />
      <Section title="Document Access Requests" items={accessRequests} collection="access_requests" render={(m) => (
        <div>
          <p className="text-ivory font-medium">{m.name} <span className="text-white/40 text-sm">· {m.email}</span></p>
          <p className="text-xs text-gold font-mono tracking-wider mt-0.5">{m.resource_title || m.document_title} {m.request_code && `· ${m.request_code}`} {m.company && `· ${m.company}`}</p>
          {m.reason && <p className="text-sm text-white/55 mt-2">{m.reason}</p>}
        </div>
      )} />
    </div>
  );
}

export function SettingsPanel({ settings, reload }) {
  const [form, setForm] = useState(settings);

  const save = async () => {
    await api.put("/admin/settings", { founder_email: form.founder_email || "", linkedin: form.linkedin || "", portrait_url: form.portrait_url || "", owner_email: form.owner_email || "" });
    toast.success("Settings saved");
    reload();
  };

  return (
    <div data-testid="settings-panel" className="max-w-xl">
      <h2 className="font-serif text-2xl text-ivory mb-6">Founder Profile & Settings</h2>
      <div className="grid gap-5">
        <div><label className={labelCls}>Public Contact Email</label><input data-testid="settings-email-input" className={inputCls} value={form.founder_email || ""} onChange={(e) => setForm({ ...form, founder_email: e.target.value })} /></div>
        <div>
          <label className={labelCls}>Notification Email (Admin Inbox)</label>
          <input data-testid="settings-owner-email-input" className={inputCls} placeholder="Where form & investor notifications are sent" value={form.owner_email || ""} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} />
          <p className="text-xs text-white/30 mt-2">Contact forms, partnership inquiries and investor access requests are emailed here. Until set, submissions are safely stored in the dashboard only.</p>
        </div>
        <div><label className={labelCls}>LinkedIn URL</label><input data-testid="settings-linkedin-input" className={inputCls} value={form.linkedin || ""} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} /></div>
        <div>
          <label className={labelCls}>Founder Portrait</label>
          <UploadField testid="settings-portrait-input" value={form.portrait_url || ""} onChange={(v) => setForm({ ...form, portrait_url: v })} />
          <p className="text-xs text-white/30 mt-2">Upload the professional founder photo here — it replaces the reserved placeholder in the hero.</p>
        </div>
        <button data-testid="settings-save-btn" onClick={save} className="w-max px-8 py-3 bg-gold text-ink font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-light">Save Settings</button>
      </div>
    </div>
  );
}
